// Preprod pick: Preprod: Centrifugo pub/sub when BE config sets pubsub to centrifugo.
import { Centrifuge } from 'centrifuge';
import {getKey} from "@utils/storage";
import { LOCALSTORAGE, FEATURES} from "@constants";
import {getPubnubChannelName, isUserloggedIn} from "@utils/common";
import { generateCentrifugoToken } from "@utils/apiService";

let centrifuge = null;
let subscription = null;
let messageCallback = null;
let pendingChannel = null;
let pendingLogin = false;

export default async (channel, callback, login) => {
    console.log("[CENTRIFUGO] Starting with channel:", channel, "login:", login);
    messageCallback = callback;
    pendingChannel = channel;
    pendingLogin = login;
    
    if (centrifuge && centrifuge.state === 'connected') {
        // Already connected, subscribe immediately
        console.log("[CENTRIFUGO] Already connected, subscribing immediately");
        await subscribe(channel);
        setListener(callback);
        if (isUserloggedIn() && !login) {
            console.log("[CENTRIFUGO] User logged in, fetching messages");
            fetchMessages();
        }
    } else {
        // Not connected yet, init will handle subscription in connected callback
        await init();
        if (centrifuge && FEATURES.is_pubnub_enabled) {
            // Subscription will happen in connected callback
        } else {
            console.warn("[CENTRIFUGO] Initialization failed or feature disabled", {
                centrifuge: !!centrifuge,
                featureEnabled: FEATURES.is_pubnub_enabled
            });
        }
    }
}

const init = async () => {
    try {
        console.log("[CENTRIFUGO] Initialization started");
        let userInfo = JSON.parse(getKey(LOCALSTORAGE.USER_INFO)) || {};
        const channelName = getPubnubChannelName();
        
        if (!channelName) {
            console.error("[CENTRIFUGO] Channel name not available");
            return;
        }

        console.log("[CENTRIFUGO] Channel name:", channelName);
        console.log("[CENTRIFUGO] Calling token generation API...");
        
        // Generate token from API - response includes token and serverUrl
        let tokenResponse;
        try {
            tokenResponse = await generateCentrifugoToken(channelName);
            console.log("[CENTRIFUGO] Token API response received:", tokenResponse);
        } catch (apiError) {
            console.error("[CENTRIFUGO] Token API call failed:", apiError);
            console.error("[CENTRIFUGO] Error details:", {
                message: apiError?.message,
                response: apiError?.response?.data,
                status: apiError?.response?.status
            });
            return;
        }
        
        const responseData = tokenResponse?.data || tokenResponse;
        console.log("[CENTRIFUGO] Response data:", responseData);
        
        const token = responseData?.data?.token || responseData?.token;
        const centrifugoUrl = responseData?.data?.serverUrl || responseData?.serverUrl;
        
        console.log("[CENTRIFUGO] Token extracted:", token ? `${token.substring(0, 20)}...` : "NOT FOUND");
        console.log("[CENTRIFUGO] Server URL extracted:", centrifugoUrl || "NOT FOUND");
        
        if (!token) {
            console.error("[CENTRIFUGO] Token generation failed - token not found in response");
            console.error("[CENTRIFUGO] Full response:", JSON.stringify(responseData, null, 2));
            return;
        }
        
        if (!centrifugoUrl) {
            console.error("[CENTRIFUGO] Server URL not found in token response");
            console.error("[CENTRIFUGO] Full response:", JSON.stringify(responseData, null, 2));
            return;
        }
        
        console.log("[CENTRIFUGO] Creating Centrifuge instance with URL:", centrifugoUrl);
        centrifuge = new Centrifuge(centrifugoUrl, {
            token: token,
            getToken: async function() {
                console.log("[CENTRIFUGO] Token refresh requested");
                const newTokenResponse = await generateCentrifugoToken(channelName);
                const newResponseData = newTokenResponse?.data || newTokenResponse;
                const newToken = newResponseData?.data?.token || newResponseData?.token;
                console.log("[CENTRIFUGO] New token generated:", newToken ? `${newToken.substring(0, 20)}...` : "FAILED");
                return newToken;
            }
        });

        // Set up event handlers
        centrifuge.on('disconnected', function(ctx) {
            console.log("[CENTRIFUGO] Disconnected", ctx);
        });

        centrifuge.on('error', function(ctx) {
            console.error("[CENTRIFUGO] Connection error", ctx);
        });

        // Subscribe to channels AFTER connection is established (like iOS does in onConnected)
        // Wait for connection to be established
        await new Promise((resolve) => {
            centrifuge.on('connected', async function(ctx) {
                console.log("[CENTRIFUGO] Connected successfully", ctx);
                console.log("[CENTRIFUGO] Connection established, subscribing to channel");
                
                // Subscribe to pending channel (matching iOS pattern - subscribe in onConnected)
                if (pendingChannel && FEATURES.is_pubnub_enabled) {
                    await subscribe(pendingChannel);
                    setListener(messageCallback);
                    if (isUserloggedIn() && !pendingLogin) {
                        console.log("[CENTRIFUGO] User logged in, fetching messages");
                        fetchMessages();
                    }
                }
                resolve();
            });

            console.log("[CENTRIFUGO] Attempting to connect...");
            centrifuge.connect();
        });
    } catch (error) {
        console.error("[CENTRIFUGO] Initialization error:", error);
        console.error("[CENTRIFUGO] Error stack:", error.stack);
    }
};

const subscribe = async (channel) => {
    if (!centrifuge) {
        console.error("[CENTRIFUGO] Cannot subscribe - not initialized");
        return;
    }
    
    console.log("[CENTRIFUGO] Subscribing to channel:", channel);
    console.log("[CENTRIFUGO] Connection state:", centrifuge.state);
    
    // Wait for connection if not already connected
    if (centrifuge.state !== 'connected') {
        console.log("[CENTRIFUGO] Waiting for connection before subscribing...");
        await new Promise((resolve) => {
            if (centrifuge.state === 'connected') {
                resolve();
            } else {
                centrifuge.on('connected', function() {
                    console.log("[CENTRIFUGO] Connection ready, proceeding with subscription");
                    resolve();
                });
            }
        });
    }
    
    // Unsubscribe from previous channel if exists
    if (subscription) {
        console.log("[CENTRIFUGO] Unsubscribing from previous channel");
        subscription.unsubscribe();
        subscription.removeAllListeners();
        subscription = null;
    }
    
    console.log("[CENTRIFUGO] Creating subscription to channel:", channel);
    
    // Use the official API: newSubscription() then subscribe()
    subscription = centrifuge.newSubscription(channel);
    
    // Set up event handlers BEFORE calling subscribe()
    subscription.on('publication', function(ctx) {
        console.log("[CENTRIFUGO] New message received!!");
        console.log("[CENTRIFUGO] Publication context:", ctx);
        console.log("[CENTRIFUGO] Message data:", ctx.data);
        
        // Centrifugo message structure: ctx.data contains the actual payload
        let messageData = ctx.data;
        
        // If ctx.data is a string, try to parse it as JSON
        if (typeof messageData === 'string') {
            try {
                messageData = JSON.parse(messageData);
                console.log("[CENTRIFUGO] Parsed message data from string:", messageData);
            } catch (e) {
                console.warn("[CENTRIFUGO] Failed to parse message data as JSON:", e);
            }
        }
        
        if (messageCallback && messageData) {
            console.log("[CENTRIFUGO] Calling handlePubnubData with:", messageData);
            messageCallback(messageData);
        } else {
            console.warn("[CENTRIFUGO] No callback or message data missing", {
                hasCallback: !!messageCallback,
                hasData: !!messageData
            });
        }
    });
    
    subscription.on('subscribed', function(ctx) {
        console.log("[CENTRIFUGO] Successfully subscribed to channel:", channel);
        console.log("[CENTRIFUGO] Subscription context:", ctx);
    });
    
    subscription.on('unsubscribed', function(ctx) {
        console.log("[CENTRIFUGO] Unsubscribed from channel:", channel);
        console.log("[CENTRIFUGO] Unsubscribe context:", ctx);
    });
    
    subscription.on('error', function(ctx) {
        console.error("[CENTRIFUGO] Subscription error on channel:", channel);
        console.error("[CENTRIFUGO] Error context:", ctx);
    });
    
    // Explicitly call subscribe() to activate the subscription
    console.log("[CENTRIFUGO] Activating subscription...");
    subscription.subscribe();
};

const setListener = (callback) => {
    // Kept for compatibility with PubNub pattern
};

export const removeCentrifugoListener = () => {
    console.log("[CENTRIFUGO] Removing listener and disconnecting");
    if (subscription) {
        console.log("[CENTRIFUGO] Unsubscribing from channel");
        subscription.unsubscribe();
        subscription.removeAllListeners();
        subscription = null;
    }
    if (centrifuge) {
        console.log("[CENTRIFUGO] Disconnecting");
        centrifuge.disconnect();
        centrifuge = null;
    }
    console.log("[CENTRIFUGO] Cleanup completed");
};

export const fetchMessages = () => {
    // Centrifugo doesn't have a direct history API like PubNub
    // History needs to be fetched from backend API if needed
    console.log("[CENTRIFUGO] History fetch not implemented");
};

